import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, catchError, first, of, switchMap, tap, timeout } from 'rxjs';
import { SoftBanFacade } from 'src/app/core/state/facades/soft-ban.facade';
import { AccountService } from 'src/app/shared/services/account.service';
import { SoftBanService } from 'src/app/shared/services/soft-ban.service';

@Injectable({
  providedIn: 'root',
})
export class SoftBanGuard {
  constructor(
    private router: Router,
    private softBanService: SoftBanService,
    private accountService: AccountService,
    private softBanFacade: SoftBanFacade,
  ) {}

  /**
   * This function helps to find out if the current user role is in the list of exempt roles.
   *
   * @param exemptRoles - Array of roles exempt from soft ban.
   * @returns - True if current user role is in list of exempt roles.
   */
  private isUserRoleExempted(exemptRoles: Array<string>): boolean {
    const currentUserRole = this.accountService.getUserType();
    return !!exemptRoles.find((exemptRole) => exemptRole.toLowerCase() === currentUserRole.toLowerCase());
  }

  /**
   * Provides all possible soft ban configuration parameters as a single object.
   *
   * @param next - ActivatedRouteSnapshot
   * @returns - object of soft-ban configuration parameters
   */
  private softBanConfigFactory(next: ActivatedRouteSnapshot) {
    // Resolves full route path to string from ActivatedRouteSnapshot
    // Extracts resolved parameters or parameters placeholders - controlled by mode
    const routeExtractFullPath = (snapshot: ActivatedRouteSnapshot, mode: 'exact' | 'param' = 'exact'): string =>
      snapshot.pathFromRoot
        .filter((snapshotElement) => snapshotElement.url.length > 0)
        .map((snapshotElement) => (mode === 'exact' ? snapshotElement.url.join('/') : snapshotElement.routeConfig.path))
        .join('/');

    // Controls the guard behaviour. If set to true then the guard will allow navigating to the selected route.
    const permissive = !!next.data?.softBanPermissive;

    // Controls the guard behaviour. If set to true then the guard will show soft ban modal once and allow selected route.
    const showModalOnce = !!next.data?.softBanShowModalOnce;

    // Controls full URL extraction from ActivatedRouteSnapshot. Intended for use with softBanShowModalOnce.
    const routeExtractMode: 'exact' | 'param' = next.data?.softBanURLmatching || 'exact';

    // Controls the roles that are allowed to navigate to protected route.
    const exemptRoles: Array<string> = next.data.softBanExemptRoles || [];

    // Controls to manage force redirection to root page.
    const softBanForceRedirectToRoot = !!next.data?.softBanForceRedirectToRoot;

    const currentRoutePath = routeExtractFullPath(next, routeExtractMode);
    const fallbackRedirection = next.data?.fallbackRedirection || '/';

    return {
      permissive,
      showModalOnce,
      exemptRoles,
      currentRoutePath,
      fallbackRedirection,
      softBanForceRedirectToRoot,
    };
  }

  /**
   * This is the function that holds the logical implementation of the soft-ban guard.
   * This is meant to be used inside the canActivate method.
   *
   * @param softBanActivated - the status of the soft ban from the store
   * @param next - Activated route snapshot
   * @returns - boolean if route can be activated or UrlTree if client needs to be redirected elsewhere.
   */
  private canActivateWhenSoftBanned(
    softBanActivated: boolean,
    next: ActivatedRouteSnapshot,
  ): Observable<boolean | UrlTree> {
    /**
     * This guard is based on soft-ban service and is designed to prevent soft banned users from
     * accessing certain url paths and/or show them modals to notify that they've been soft banned.
     *
     * This guard has a couple flags that can alter its behaviour:
     *
     * softBanShowModalOnce - is useful when it is required to show soft ban modal once on a given page
     * and allow further navigation (i.e.: on dashboard after successful login).
     *
     * SoftBanURLmatching - is designed to extend functionality of softBanShowModalOnce feature.
     * - matching exact (default) returns full URL with the actual parameters: profile/user_xyz
     * - matching param returns full URL with parameters (placeholders)  i.e.: profile/:id.
     *
     * SoftBanPermissive - is useful when it is not required to protect the route from the banned user
     * but rather just show them the modal that they've been soft banned.
     *
     * SoftBanExemptRoles - array of roles that are allowed to access the current route irrespective of
     * the user soft ban status.
     */
    const softBanConfig = this.softBanConfigFactory(next);

    if (softBanActivated && !this.isUserRoleExempted(softBanConfig.exemptRoles)) {
      // Handles the cases of showing modal only once per session i.e.: once after login and allowing navigation.
      if (softBanConfig.showModalOnce) {
        return this.softBanFacade.canShowModal$(softBanConfig.currentRoutePath).pipe(
          first(),
          tap((canShowModal) => {
            if (canShowModal) {
              this.softBanService.openSoftBanModal();
              this.softBanFacade.addModalShownRoute('SoftBanGuard', softBanConfig.currentRoutePath);
            }
          }),
          switchMap(() => of(true)),
        );
      }
      // Changes behaviour of the guard to allow navigation to routes with softBanPermissive flag set to true.
      if (softBanConfig.permissive) {
        this.softBanService.openSoftBanModal();
        return of(true);
      }

      // Check if force redirection is configured than redirect to root page.
      if (softBanConfig.softBanForceRedirectToRoot) {
        return of(this.router.parseUrl('/'));
      }

      // For checking if there was any previous navigation or user accessed page directly i.e.: using bookmark or browser history.
      if (this.router.getCurrentNavigation().previousNavigation) {
        this.softBanService.openSoftBanModal();
        return of(false);
      }
      // If person is soft banned, navigated straight to the page (i.e.: from bookmark)
      // and not allowed to proceed this will force navigation to the fallback URL or dashboard.
      return of(this.router.parseUrl(softBanConfig.fallbackRedirection));
    }
    return of(true);
  }

  canActivate(next: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return this.softBanFacade.isSoftBanActive$.pipe(
      timeout({ first: 10000 }),
      first(),
      switchMap((softBanActivated) => this.canActivateWhenSoftBanned(softBanActivated, next)),
      catchError((error) => {
        // returns TRUE if failed to initialise
        console.error('[SoftBanGuard] Failed to initialise', error);
        return of(true);
      }),
    );
  }
}
